import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  X,
  Pin,
  RefreshCw,
  Sparkles,
  Loader2,
  ChevronDown,
  FileText,
} from "lucide-react";
import { TerminalTheme } from "../types";

type ScopedFetch = (path: string, init?: RequestInit) => Promise<Response>;

interface Props {
  theme: TerminalTheme;
  scopedFetch?: ScopedFetch;
  onClose: () => void;
  /** Called with the doc filename when a card is tapped; parent surfaces
   *  MobileDocViewerPane for the chosen doc. */
  onOpenDoc: (filename: string) => void;
}

// Subset of DocRecord shape returned by /api/documents/{pinned,recent}.
// The mobile drawer needs less than DocumentsPage — name, title, category,
// age, brand_voice_id, ai_touched — so we type only what we render.
interface DocRecord {
  id: string;
  name: string;
  title: string;
  category: string;
  status: string;
  pinned: boolean;
  archived: boolean;
  brand_voice_id?: string | null;
  age_days: number;
  ai_touched: boolean;
}

interface CategoryEntry {
  id: string;
  label: string;
  emoji: string;
  color: string;
}

// MobileDocsDrawer — bottom-sheet pulled up from the chat header's drawer
// menu. ADHD-friendly: only two sections, Pinned + Recent, no category
// drill-in (per Terrell's directive). Tap a card → opens MobileDocViewerPane
// in the parent. Swipe-down on the drag handle OR tap-outside to close.
//
//   ┌────────────────────────────────┐
//   │  ─── drag handle ───           │
//   │  Documents          ⟳    ✕     │
//   ├────────────────────────────────┤
//   │  📌 Pinned (3/6)               │
//   │  [card] [card] [card]          │
//   ├────────────────────────────────┤
//   │  🔥 Recent (last 10)           │
//   │  [card] [card] …               │
//   └────────────────────────────────┘
//
// Card layout: title + emoji (category) + age + brand_voice + ✨ if ai_touched.

function ageLabel(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export const MobileDocsDrawer: React.FC<Props> = ({
  theme,
  scopedFetch,
  onClose,
  onOpenDoc,
}) => {
  const [pinned, setPinned] = useState<DocRecord[]>([]);
  const [recent, setRecent] = useState<DocRecord[]>([]);
  const [categories, setCategories] = useState<CategoryEntry[]>([]);
  const [pinnedMax, setPinnedMax] = useState<number>(6);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const dragStartY = useRef<number | null>(null);

  const fetcher: ScopedFetch = scopedFetch || ((p, init) => fetch(p, init));

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [catsRes, pinRes, recRes] = await Promise.all([
        fetcher("/api/documents/categories"),
        fetcher("/api/documents/pinned"),
        fetcher("/api/documents/recent?n=10"),
      ]);

      if (catsRes.ok) {
        const d = await catsRes.json();
        setCategories(Array.isArray(d?.categories) ? d.categories : []);
      }
      if (pinRes.ok) {
        const d = await pinRes.json();
        setPinned(Array.isArray(d?.items) ? d.items : []);
        if (typeof d?.max === "number") setPinnedMax(d.max);
      } else {
        throw new Error(`pinned: HTTP ${pinRes.status}`);
      }
      if (recRes.ok) {
        const d = await recRes.json();
        setRecent(Array.isArray(d?.items) ? d.items : []);
      } else {
        throw new Error(`recent: HTTP ${recRes.status}`);
      }
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    load();
  }, [load]);

  const onTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.changedTouches[0].clientY - dragStartY.current;
    if (delta > 80) onClose();
    dragStartY.current = null;
  };

  const emojiFor = useCallback(
    (cat: string): string => {
      const found = categories.find((c) => c.id === cat);
      return found?.emoji || "📄";
    },
    [categories]
  );

  // ─── Card ──────────────────────────────────────────────────────────
  const Card: React.FC<{ doc: DocRecord }> = ({ doc }) => (
    <button
      type="button"
      onClick={() => onOpenDoc(doc.name)}
      style={{
        backgroundColor: theme.inputBg,
        borderColor: theme.border,
        color: theme.fg,
      }}
      className="text-left border rounded-xl p-3 transition-opacity active:opacity-70 w-full"
      title={doc.title}
    >
      <div
        className="text-[14px] font-semibold leading-tight line-clamp-2"
        style={{ color: theme.fg }}
      >
        {doc.title || doc.name}
      </div>
      <div
        className="text-[11px] mt-2 flex items-center gap-1.5 flex-wrap"
        style={{ color: theme.systemText }}
      >
        <span>{emojiFor(doc.category)}</span>
        <span>·</span>
        <span>{ageLabel(doc.age_days)}</span>
        {doc.ai_touched && (
          <>
            <span>·</span>
            <span className="inline-flex items-center gap-0.5">
              <Sparkles size={10} /> AI
            </span>
          </>
        )}
        {doc.status === "draft" && (
          <>
            <span>·</span>
            <span
              className="uppercase font-bold text-[9px] px-1.5 py-0.5 rounded"
              style={{
                backgroundColor: `${theme.accent}25`,
                color: theme.accent,
              }}
            >
              draft
            </span>
          </>
        )}
      </div>
      {doc.brand_voice_id && (
        <div
          className="text-[10px] mt-1 truncate"
          style={{ color: theme.systemText }}
        >
          🎨 {doc.brand_voice_id}
        </div>
      )}
    </button>
  );

  const EmptySection: React.FC<{ text: string }> = ({ text }) => (
    <div
      className="text-[13px] py-6 px-3 rounded-xl text-center"
      style={{
        color: theme.systemText,
        backgroundColor: theme.inputBg,
        border: `1px dashed ${theme.border}`,
      }}
    >
      {text}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ backgroundColor: theme.bg, color: theme.fg }}
        className="w-full h-[88dvh] rounded-t-2xl flex flex-col overflow-hidden animate-[slideUp_200ms_ease-out]"
      >
        {/* Drag handle + header */}
        <div
          className="flex flex-col items-center pt-2 pb-1 flex-shrink-0"
          style={{ borderBottom: `1px solid ${theme.border}` }}
        >
          <div
            className="w-12 h-1 rounded-full"
            style={{ backgroundColor: theme.systemText, opacity: 0.4 }}
          />
          <div className="w-full flex items-center justify-between px-4 pt-2 pb-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <FileText size={16} style={{ color: theme.accent }} />
              <span
                className="text-[16px] font-semibold truncate"
                style={{ color: theme.fg }}
              >
                Documents
              </span>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              aria-label="Refresh"
              className="p-2 disabled:opacity-50"
              style={{ color: theme.systemText, minWidth: 44, minHeight: 44 }}
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <RefreshCw size={18} />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="p-2 -mr-2"
              style={{ color: theme.systemText, minWidth: 44, minHeight: 44 }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          className="flex-1 overflow-y-auto px-4 py-3 min-h-0"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {err && (
            <div
              className="text-[13px] px-3 py-2 rounded mb-3"
              style={{ backgroundColor: "#d6243a20", color: "#d6243a" }}
            >
              {err}
            </div>
          )}

          {/* Pinned section */}
          <section className="mb-5">
            <div
              className="flex items-center justify-between mb-2"
              style={{ color: theme.systemText }}
            >
              <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-wider">
                <Pin size={12} /> Pinned
              </div>
              <div className="text-[11px]">
                {pinned.length}/{pinnedMax}
              </div>
            </div>
            {loading && pinned.length === 0 ? (
              <div
                className="text-[13px] py-4 px-3 flex items-center gap-2"
                style={{ color: theme.systemText }}
              >
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            ) : pinned.length === 0 ? (
              <EmptySection text="Nothing pinned yet. Pin docs from the desktop Documents tab." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {pinned.map((doc) => (
                  <Card key={doc.name} doc={doc} />
                ))}
              </div>
            )}
          </section>

          {/* Recent section */}
          <section>
            <div
              className="flex items-center justify-between mb-2"
              style={{ color: theme.systemText }}
            >
              <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-wider">
                <span>🔥</span> Recent
              </div>
              <div className="text-[11px]">last {recent.length}</div>
            </div>
            {loading && recent.length === 0 ? (
              <div
                className="text-[13px] py-4 px-3 flex items-center gap-2"
                style={{ color: theme.systemText }}
              >
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            ) : recent.length === 0 ? (
              <EmptySection text="No recent documents yet." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {recent.map((doc) => (
                  <Card key={doc.name} doc={doc} />
                ))}
              </div>
            )}
          </section>

          {/* Spacer so last card isn't flush with the bottom edge */}
          <div className="h-4" />
        </div>

        {/* Footer hint */}
        <div
          className="px-4 py-2 text-[10px] flex items-center justify-center gap-1 flex-shrink-0"
          style={{
            color: theme.systemText,
            borderTop: `1px solid ${theme.border}`,
          }}
        >
          <ChevronDown size={10} /> Swipe down or tap outside to close
        </div>
      </div>
    </div>
  );
};
